
import { computed, defineComponent, ref, watch } from 'vue';
import { RuntimeO2MField } from '@kunlun/dependencies';
import {
  buildCreateDefaultStarRules,
  formatStarRange,
  IStarRule,
  starLevelText
} from './constants';

export default defineComponent({
  name: 'EvaluationStarRuleTable',
  props: {
    formData: {
      type: Object,
      default: () => ({})
    },
    change: {
      type: Function,
      default: () => {}
    },
    _field: {
      type: Object as () => RuntimeO2MField,
      default: () => ({} as RuntimeO2MField)
    },
    isReadonly: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const starRules = ref<IStarRule[]>([]);

    const fieldName = () => (props._field && props._field.name) || 'starRules';

    /**
     * 归一化：动态行数，按已保存行原样展示（不再强制补齐五档）。
     */
    const normalize = (list: any[]): IStarRule[] => {
      const rows: IStarRule[] = [];
      if (Array.isArray(list)) {
        list.forEach((item) => {
          if (!item) {
            return;
          }
          const lv = item.starLevel == null ? null : Number(item.starLevel);
          rows.push({
            starLevel: lv != null && Number.isInteger(lv) && lv >= 1 ? lv : null,
            minScore: item.minScore ?? null,
            maxScore: item.maxScore ?? null,
            description: item.description ?? ''
          });
        });
      }
      return rows;
    };

    const serialize = (list: IStarRule[]) =>
      list.map((r) => ({
        starLevel: r.starLevel,
        minScore: r.minScore,
        maxScore: r.maxScore,
        description: r.description
      }));

    const emitChange = () => {
      if (props.isReadonly) {
        return;
      }
      props.change(serialize(starRules.value));
    };

    /** 保证至少 1 行：仅剩 1 行时删除按钮置灰 */
    const canDelete = computed(() => starRules.value.length > 1);

    const addRow = () => {
      if (props.isReadonly) {
        return;
      }
      starRules.value.push({ starLevel: null, minScore: null, maxScore: null, description: '' });
      emitChange();
    };

    const removeRow = (index: number) => {
      if (props.isReadonly || !canDelete.value) {
        return;
      }
      starRules.value.splice(index, 1);
      emitChange();
    };

    // 初始化：拿到首个非空值后同步一次并停止；
    // 只读模式下持续同步（不会回写，无覆盖风险）
    let seeded = false;
    const stopInit = watch(
      () => props.formData?.[fieldName()],
      (val) => {
        if (Array.isArray(val) && val.length) {
          starRules.value = normalize(val);
          seeded = true;
          if (!props.isReadonly) {
            stopInit();
          }
        }
      },
      { deep: true, immediate: true }
    );

    // 若表单尚无 starRules：仅在新建场景（无 id、非只读）播种默认 1 行一星并回写；
    // 编辑/详情场景不播种，避免覆盖异步加载的已有数据
    if (!seeded && !props.isReadonly && !props.formData?.id) {
      starRules.value = buildCreateDefaultStarRules();
      emitChange();
    }

    return {
      starRules,
      canDelete,
      addRow,
      removeRow,
      starLevelText,
      formatStarRange,
      emitChange
    };
  }
});
